import AccountService from "./AccountService.js";
import AccountSync from "./AccountSync.js";
import { renderAvatarSVG, loadStoredAvatar } from "../utils/AvatarBuilder.js";

/**
 * Bloc « compte » de la barre de navigation : avatar + pseudo et son menu
 * déroulant quand on est connecté, liens « S'inscrire / Connexion » sinon.
 *
 * Le HTML servi montre l'état ANONYME (celui d'un visiteur non connecté, donc
 * de tout crawler) ; l'état connecté n'apparaît qu'une fois la session connue,
 * ce qui n'arrive qu'après un aller-retour avec l'API. L'inverse ferait
 * clignoter un menu de compte chez qui n'en a pas.
 *
 * L'ouverture/fermeture du menu lui-même est gérée par `HeaderMenu.js`, comme
 * pour les autres sous-menus.
 */
export default class AccountMenu {
    constructor() {
        this.root = document.querySelector(".site-header .account-menu");
        if (!this.root) return;

        this.anon = this.root.querySelector(".account-anon");
        this.user = this.root.querySelector(".account-user");
        this.icon = this.root.querySelector(".account-icon");
        this.name = this.root.querySelector(".account-name");

        this.root.querySelector(".account-logout")?.addEventListener("click", () => this.handleLogout());

        // Pas d'appel synchrone ici : `.account-menu` est masqué (`.is-checking`,
        // posé par le script inline de _header.html) tant qu'on ne sait pas
        // encore si la session est valide. Basculer sur l'état anonyme puis sur
        // le connecté produisait un saut visible à chaque chargement pour un
        // joueur connecté ; on attend la vraie réponse avant de rien montrer.
        window.geoNerdApp.accountReady?.then(() => this.refreshState());
        // Émis par la page avatar à la validation : l'icône de la barre doit suivre.
        document.addEventListener("geonerd:avatarchange", () => this.refreshState());
    }

    refreshState() {
        this.root.classList.remove("is-checking");
        const loggedIn = AccountSync.isLoggedIn();
        // `.is-logged-in` sert au dimensionnement du bloc compte en mobile
        // (voir _header.scss) ; `hidden` fait le vrai travail d'affichage — le
        // bloc prend la taille de l'état qui s'affiche, quitte à décaler le
        // son/la langue voisins au moment de la bascule.
        this.root.classList.toggle("is-logged-in", loggedIn);
        if (this.anon) this.anon.hidden = loggedIn;
        if (this.user) this.user.hidden = !loggedIn;
        if (!loggedIn) return;

        if (this.icon) this.icon.innerHTML = renderAvatarSVG(loadStoredAvatar(), 28);
        if (this.name) {
            this.name.textContent = AccountSync.user.username
                || window.geoNerdApp.languageManager.t("profile.noUsername");
        }
    }

    async handleLogout() {
        await AccountService.logout();
        AccountSync.clear();
        window.location.reload();
    }
}
